// permission.guard.ts
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DashboardModuleService } from '../modules/dashboard/services/dashboard-module.service';
import { PERMISSION_TYPE } from '../modules/shared/enums/common.enum';
import { Application } from '../enums/application.enums';
import { PermissionService } from '../modules/shared/services/permissions.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  private userData$ = new BehaviorSubject<any>(null);
  destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private dashboardService: DashboardModuleService,
    private permissionService: PermissionService,
    @Inject(PLATFORM_ID) private platformId: Object // Add type for platformId
  ) {
    this.dashboardService.userProfileSubscription()
    .pipe(takeUntil(this.destroy$))
    .subscribe((userData) => {
      this.userData$.next(userData);
    });
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const path: any = route.data['path'];
    const delegatePath = route.data['delegatePath'];
    const checkDelegate = !!route.data['checkDelegateUser'];
    const checkMyProfile = !!route.data['myProfileAllowed'];
    const routeKey = route.data['routeKey'];

    const action: PERMISSION_TYPE.READ | PERMISSION_TYPE.WRITE =
      route.data['action'] || PERMISSION_TYPE.READ;
    const routeData = {
      fullUrl: state.url,
      routeParams: route.params,
      queryParams: route.queryParams,
      routeKey
    };
    if (isPlatformBrowser(this.platformId)) {
      return this.userData$.pipe(
        switchMap((userData) => {
          return this.permissionService
            .checkUserPermissions(
              userData,
              path,
              action,
              delegatePath,
              checkDelegate,
              checkMyProfile,
              routeData
            )
            .pipe(
              tap((hasAccess) => {
                if (!hasAccess) {
                  this.router.navigate([Application.dashboardDiscover]);
                }
              }),
              catchError((err) => {
                console.error('Error in Permission Guard:', err);
                this.router.navigate([Application.dashboardDiscover]);
                return of(false);
              })
            );
        })
      );
    }
    return of(true);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
